import React, { useEffect } from "react";
import Layout from "../../../components/Layout";
import DataTable from "../../../components/DataTable";
import useData from "../../../hooks/useData";
import ViewLog from "../../../components/audit-logs/View";
import FilterLogs from "../../../api/audit-logs/filterLogs";
import SearchFilter from "../../../components/filters/SearchFilter";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import { EyeOpen } from "../../../assets/icons/Icons";
import ResetFilters from "../../../components/filters/ResetFilters";
import { formatDate } from "../../../utils/formatDate";
import DateRangeFilter from "../../../components/filters/DateRangeFilter";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SelectFilter from "../../../components/filters/SelectFilter";
import CustomTooltip from "../../../components/CustomTooltip";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import { sanitizeHtml } from "../../../utils/sanitizeHtml";

const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

function Index(props) {
  const { t } = useTranslation();

  const {
    data,
    queryParams,
    errorMessage,
    loadingData,
    pageCount,
    selectedItem,
    setSelectedItem,
    setShowViewItem,
    showViewItem,
  } = useData(FilterLogs);

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Settings / Global Settings"));
    setBreadcrumbs([t("Settings"), t("Global Settings"), t("Audit Logs")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Layout title={t("Audit Logs")} breadcrumbs={t("Settings/Global Settings")}>
      {showViewItem && (
        <ViewLog log={selectedItem} onClose={() => setShowViewItem(false)} />
      )}

      <DataTable
        filtersTop={
          <>
            <FilterContextProvider name="search_in_fields">
              <SelectFilter
                options={[
                  {
                    value: "email",
                    label: t("Email"),
                  },
                  {
                    value: "in_request",
                    label: t("Request"),
                  },
                ]}
                defaultValue="email"
                placeholder={<div>{t("Search in fields")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider name="s">
              <SearchFilter
                placeholderText={t("Search")}
                inputStyles={{ width: "400px", maxWidth: "100%" }}
              />
            </FilterContextProvider>
          </>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="statusCode">
              <SelectFilter
                options={[
                  {
                    value: "500",
                    label: t("Error"),
                  },
                  {
                    value: "404",
                    label: t("Not Found"),
                  },
                  {
                    value: "200",
                    label: t("Success"),
                  },
                ]}
                placeholder={<div>{t("Status code")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider name="action">
              <SelectFilter
                options={[
                  {
                    value: "request",
                    label: t("Request"),
                  },
                  {
                    value: "updated",
                    label: t("Updated"),
                  },
                  {
                    value: "created",
                    label: t("Created"),
                  },
                  {
                    value: "deleted",
                    label: t("Deleted"),
                  },
                ]}
                placeholder={<div>{t("Action")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider name="date">
              <DateRangeFilter />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={<></>}
        columns={[
          { header: t("Email"), accessor: "email" },
          { header: t("Action"), accessor: "method" },
          { header: t("URL"), accessor: "originalUrl" },
          { header: t("Request Body"), accessor: "requestBody" },
          { header: t("Timestamp"), accessor: "@timestamp" },
          { header: t("Actions"), accessor: "actions" },
        ]}
        rows={data.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        emptyDataMessage={t("No logs found")}
        actions={(item) => (
          <div className="d-flex align-items-center">
            <Link
              to="#"
              className="mr-1"
              onClick={(e) => {
                e.preventDefault();
                setSelectedItem(item);
                setShowViewItem(true);
              }}
            >
              <CustomTooltip icon={<EyeOpen />} message={t("View")} />
            </Link>
          </div>
        )}
        customOutputs={(columnName, value, row) => {
          switch (columnName) {
            case "method":
              switch (value) {
                case "GET":
                  return (
                    <span style={{ color: "#14aa40", fontWeight: 700 }}>
                      {t("Request")}
                    </span>
                  );
                case "POST":
                  return (
                    <span style={{ color: "#3982E8", fontWeight: 700 }}>
                      {t("Created")}
                    </span>
                  );
                case "PATCH":
                case "PUT":
                  return (
                    <span style={{ color: "#FFB50D", fontWeight: 700 }}>
                      {t("Updated")}
                    </span>
                  );
                case "DELETE":
                  return (
                    <span style={{ color: "#F66565", fontWeight: 700 }}>
                      {t("Deleted")}
                    </span>
                  );
                default:
                  return value;
              }
            case "@timestamp":
              return formatDate(value, "yyyy-MM-dd HH:mm:ss");
            case "email":
            case "requestBody":
            case "originalUrl":
              let searchString = queryParams?.s;
              let searchInFields = queryParams?.search_in_fields;
              if (
                (columnName === "email" &&
                  searchString &&
                  (searchInFields === "email" || searchInFields === "")) ||
                ((columnName === "requestBody" ||
                  columnName === "originalUrl") &&
                  searchString &&
                  searchInFields === "in_request")
              ) {
                let escapedSearch = escapeRegExp(searchString);
                let searchRegex = new RegExp(`(${escapedSearch})`, "gi");
                value = value.replace(searchRegex, "<strong>$1</strong>");
              }
              return <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />;

            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
